import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateChildFn,
  CanActivateFn,
  Router,
  UrlTree,
} from '@angular/router';

import { OrgContextService } from '../core/services/org-context.service';
import { ProductAccessService } from '../core/services/product-access.service';
import { ProjectService } from '../core/services/project.service';

/**
 * Route `data` key marking a route (or subtree) as requiring a product (C8).
 * Read by `requireProductChild` on every child navigation and by the layout's
 * mid-session revocation effect.
 */
export const REQUIRES_PRODUCT = 'requiresProduct';

/**
 * Walks from `route` up through its parents and returns the first
 * `data.requiresProduct` marker, or null when the target route is not
 * product-scoped (account, the product-disabled page itself, …).
 */
export function requiredProductFor(route: ActivatedRouteSnapshot): string | null {
  for (let node: ActivatedRouteSnapshot | null = route; node; node = node.parent) {
    const product = node.data?.[REQUIRES_PRODUCT];
    if (typeof product === 'string' && product) return product;
  }
  return null;
}

/**
 * Shared entitlement resolution (A7/C8). Captures dependencies synchronously
 * (guards lose the injection context after the first await) and denies
 * definitively: unknown state is only a denial after a definitive load, and a
 * failed load surfaces as denial rather than silently granting access.
 */
function checkProductAccess(productId: string, deniedUrl: string): Promise<true | UrlTree> {
  const orgContext = inject(OrgContextService);
  const productAccess = inject(ProductAccessService);
  const router = inject(Router);

  return (async () => {
    if (!orgContext.currentOrg()) {
      await orgContext.init().catch(() => undefined);
    }
    const org = orgContext.currentOrg();
    if (!org) return router.parseUrl('/new');

    const allowed = await productAccess.hasProduct(org.id, productId);
    return allowed ? true : router.parseUrl(deniedUrl);
  })();
}

/**
 * Entitlement guard for standalone Create routes (A7).
 *
 * `requireOrg()` only proves an organization is selected — it says nothing about
 * whether that organization has the Create product, so switching to an org
 * without Create still loaded the whole app. This guard resolves entitlement
 * from the org's `product-access/config` document and denies definitively.
 *
 * `ensureProductAccount` is deliberately NOT used here: it provisions an
 * account, it does not authorize one.
 *
 * This is UX only. The callables and Firestore rules remain the security
 * boundary.
 */
export function requireProduct(productId: string, deniedUrl = '/product-disabled'): CanActivateFn {
  return () => checkProductAccess(productId, deniedUrl);
}

/**
 * Prevents an asset URL from borrowing the active organization's entitlement
 * for a project stamped to a different organization. Unstamped projects predate
 * organization scoping and are safe to open because `getProject` reads only the
 * signed-in user's subtree; protected backend operations lazily bind those
 * owner projects to the active Create-entitled organization.
 */
export function requireProjectOrganization(
  parameter = 'projectId',
  deniedUrl = '/product-disabled',
): CanActivateFn {
  return (route) => {
    const orgContext = inject(OrgContextService);
    const projects = inject(ProjectService);
    const router = inject(Router);

    return (async () => {
      if (!orgContext.currentOrg()) await orgContext.init().catch(() => undefined);
      const orgId = orgContext.currentOrg()?.id;
      const projectId = route.paramMap.get(parameter);
      if (!orgId || !projectId) return router.parseUrl(deniedUrl);

      try {
        const project = await projects.getProject(projectId);
        return project && (!project.organizationId || project.organizationId === orgId)
          ? true
          : router.parseUrl(deniedUrl);
      } catch {
        return router.parseUrl(deniedUrl);
      }
    })();
  };
}

/**
 * Layout-level entitlement guard (C8), modeled on chat-ui's
 * `product-access.guard.ts`: re-checks entitlement on EVERY child navigation
 * inside the authed layout (sidebar links, browser back/forward, pasted URLs)
 * instead of repeating `requireProduct` on each route.
 *
 * Unlike chat-ui — where the whole layout is one product — Create's layout
 * also hosts neutral surfaces that must stay reachable without the product
 * (account and the denial page itself). Only children carrying a `data.requiresProduct`
 * marker are gated; navigation to the denied screen is always allowed.
 */
export function requireProductChild(deniedUrl = '/product-disabled'): CanActivateChildFn {
  return (childRoute, state) => {
    const productId = requiredProductFor(childRoute);
    if (!productId || state.url.startsWith(deniedUrl)) return true;
    return checkProductAccess(productId, deniedUrl);
  };
}
